import React, { useMemo } from 'react';
import { getLearningCycleStages } from '../data/learningCycleData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const LearningCyclePage = ({ language = 'traditional', onBack, onViewPackages }) => {
  const { data: remote } = useSupabaseData({
    table: 'eng_learningcycle',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const stages = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return getLearningCycleStages();
    const rows = Array.isArray(remote) ? remote : [remote];
    const out = [];
    rows.filter(r => (r.type || r.section) === 'stage').forEach((r) => {
      out.push({
        id: r.stage_id || r.id,
        position: r.position || 'bottom-center',
        title: { traditional: r.title_tc || '', simplified: r.title_sc || '', english: r.title_en || '' },
        description: { traditional: r.description_tc || '', simplified: r.description_sc || '', english: r.description_en || '' },
        icon: r.icon || '🎯',
        iconColor: r.icon_color || 'from-blue-500 to-purple-500'
      });
    });
    return out.length > 0 ? out : getLearningCycleStages();
  }, [remote]);

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  const getStagePosition = (position) => {
    switch (position) {
      case 'top-center':
        return 'justify-center';
      case 'bottom-left':
        return 'justify-start';
      case 'bottom-center':
        return 'justify-center';
      case 'bottom-right':
        return 'justify-end';
      default:
        return 'justify-center';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold">
            {getText({ traditional: '學習循環系統', simplified: '学习循环系统', english: 'Learning Cycle System' })}
          </h1>
          <button onClick={onBack} className="text-white text-xl">
            ✕
          </button>
        </div>

        {/* Learning Cycle Stages */}
        <div className="relative mb-8">
          {/* Top Center Stage */}
          <div className="flex justify-center mb-8">
            {stages.filter(stage => stage.position === 'top-center').map((stage) => (
              <div key={stage.id} className="text-center max-w-xs">
                <h3 className="text-lg font-semibold mb-2">{getText(stage.title)}</h3>
                <p className="text-sm text-gray-300 mb-4">{getText(stage.description)}</p>
                <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-r ${stage.iconColor} flex items-center justify-center text-2xl`}>
                  {stage.icon}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Row Stages */}
          <div className="grid grid-cols-3 gap-4">
            {stages.filter(stage => stage.position !== 'top-center').map((stage) => (
              <div key={stage.id} className={`flex ${getStagePosition(stage.position)}`}>
                <div className="text-center max-w-xs">
                  <h3 className="text-lg font-semibold mb-2">{getText(stage.title)}</h3>
                  <p className="text-sm text-gray-300 mb-4">{getText(stage.description)}</p>
                  <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-r ${stage.iconColor} flex items-center justify-center text-2xl`}>
                    {stage.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dashed Lines (Visual representation) */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Add dashed lines connecting stages if needed */}
            <div className="absolute top-1/2 left-1/4 w-1/2 h-px border-t-2 border-dashed border-gray-400 opacity-30"></div>
            <div className="absolute top-1/2 left-1/2 w-1/2 h-px border-t-2 border-dashed border-gray-400 opacity-30"></div>
          </div>
        </div>

        {/* Call to Action Button */}
        <div className="text-center">
          <button
            onClick={onViewPackages}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors"
          >
            {getText({ traditional: '查看學習套餐', simplified: '查看学习套餐', english: 'View Learning Package' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LearningCyclePage;
